import * as React from 'react'
import styled from 'styled-components'
import type { ColorsType } from '@components/shared/colors'
import { colors } from '@components/shared/colors'
import { Col, config, Container, Row } from 'react-awesome-styled-grid'
import TopNav from '@components/TopNav'
import { CTAPrimary, GoodSansB1, GoodSansB2, GoodSansB4, QueensH2, QueensH3 } from '@components/shared'
import { Bustle, MarieClaire, TheGuardian, TheNewYorker, Vogue, Vox } from '@components/home/PressMediaSlider'
import { useDispatch } from 'react-redux'
import { openSubscribe } from '../../actions/navigationActions'
import SubscribeFlow2 from '@components/subscribe/SubscribeFlow2'
import ThemeContext from '@components/shared/ThemeContext'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { HomeCarouselComponent } from '@components/home/Carousel'
import FadeInContent from '@components/shared/FadeInContent'
import { getCarouselTrackPropsFromTrackIds } from '../../models/Track'
import type { CarouselType } from '../../models/Track'

export type PropsType = {
  color: ColorsType,
  inlineSubscribe: boolean,
  copyText?: {
    header?: string,
    subHeader?: string,
    subBody?: string,
    benefitsArr?: string[],
    ctaText?: string,
    trialLanguage?: string
  },
  chapterImages?: string[],
  saleEnd?: string,
  quotes?: React.ReactNode[]
}

const copy = {
  header: 'Choose what you pay',
  subHeader: 'For Pride, we\'re letting you choose the subscription price that works for you. Start exploring Dipsea’s library of 1,000+ erotic audio stories—designed to turn you on, spark your imagination, and bring you closer to yourself.',
  benefitsArr: [
    'The perfect way to treat yourself',
    'Bite-sized romance gets you right to the good part',
    'Made by women for the female gaze'
  ],
  subBody: 'Select the price that’s right for you. Cancel anytime.',
  ctaText: 'SELECT',
  trialLanguage: 'ONE WEEK FREE'
}

const quotes = [
  <span key={'quote-1'}>“Not only do they have pleasurable audios but also sleep ones. On the pleasure side, I’m a WLW and I love this app enough to <b>tell all my queer friends about it</b>”</span>,
  <span key={'quote-2'}>“Great queer storylines and lots of them! Centers <b>consent and pleasure</b>. ♥️ I love this app...”</span>,
  <span key={'quote-3'}>“I love this app so much. <b>It’s inclusive</b>, with many options for genders, orientations, scenarios, ethnicities, and interests, and they’re expanding on them.”</span>,
  <span key={'quote-4'}>“Game changer. I’ve recommended this to many friends, especially those who experience responsive desire. I’m a pan lady and the <b>queer p*rn is the best I’ve ever found</b> 🙌”</span>,
  <span key={'quote-5'}>“Dipsea is exactly what I’ve been looking for without even realizing it! I love how <b>affirmative consent is a smooth and sexy part</b> of each story. Also, the stories helped me realize I’m queer? An unexpected revelation for someone in their 30s! Thank you, Dipsea team—what a gift you’ve provided!”</span>,
  <span key={'quote-5'}>“This is truly amazing. These stories are so well done and spicy in the best way! Beautiful stories for <b>queer + everyone</b> ♥️ thank you!”</span>,
  <span key={'quote-5'}>“Thank you to the folks that have put together. Even bigger thanks to the queer stories that exist here. <b>Visibility is so important</b>.”</span>]

function getColor (color: ColorsType, isCheckOut: boolean): { background: string, font: string, cta: string } {
  if (isCheckOut) {
    return {
      background: colors.midnightBlue,
      font: colors.white,
      cta: colors.orange
    }
  }
  switch (color) {
    case 'seaGreen':
      return {
        background: colors.seaGreen,
        font: colors.black,
        cta: colors.forestGreen
      }
    case 'pink':
      return {
        background: colors.pink,
        font: colors.black,
        cta: colors.orange
      }
    case 'white':
      return {
        background: colors.white,
        font: colors.black,
        cta: colors.orange
      }
    default:
      return {
        background: colors.midnightBlue,
        font: colors.white,
        cta: colors.orange
      }
  }
}

function PrideCarousel (props: {trackIds: string[]}): React.ReactNode {
  const carouselProps = {
    isCoverflow: true,
    carouselLength: 5,
    isSmall: true
  }

  const [tracks, setTracks] = React.useState<CarouselType[]>([])

  React.useEffect((): void => {
    getCarouselTrackPropsFromTrackIds(props.trackIds).then((trackItems: CarouselType[]): void => { setTracks(trackItems) })
  }, [props.trackIds])

  return <HomeCarouselComponent {...carouselProps} tracks={tracks}/>
}

export default function PrideLandingPage (props: PropsType): React.ReactNode {
  const dispatch = useDispatch()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [isInlineCheckout, setIsInlineCheckout] = React.useState<boolean>(false)
  const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false)
  React.useEffect((): void => {
    const active = searchParams.get('active')
    if (active && props.inlineSubscribe && !isInlineCheckout) {
      setIsInlineCheckout(true)
    } else {
      setIsInlineCheckout(false)
    }
  }, [searchParams])

  const openSubscribePage = (promo: string): void => {
    if (props.inlineSubscribe) {
      setIsTransitioning(true)
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })

      setTimeout((): void => {
        setIsInlineCheckout(true)
        setIsTransitioning(false)
      }, 500)

      // Create normalized URL for comparison
      const params = new URLSearchParams(searchParams.toString())
      params.set('active', 'true')
      params.set('promo', promo)
      const newUrl = `${pathname}?${params.toString()}`

      // Compare using normalized query parameters to handle encoding differences
      const currentParams = new URLSearchParams(window.location.search)
      const isActiveTrue = currentParams.get('active') === 'true'
      const currentPromo = currentParams.get('promo')

      // Only push if parameters are actually different
      if (!isActiveTrue || currentPromo !== promo) {
        router.push(newUrl, undefined, { shallow: true })
      }
    } else {
      dispatch(openSubscribe())
    }
  }
  const getTrialLanguage = <BadgeContainer $color={props.color}>{props.copyText?.trialLanguage || copy.trialLanguage}</BadgeContainer>

  const header: React.ReactNode = (
    <Container fluid={true}>
      <Row>
        <Col align={'center'}>
          <Header $color={props.color}>{props.copyText?.header || copy.header}</Header>
          <SubHeader $color={props.color}>{props.copyText?.subHeader || copy.subHeader}</SubHeader>
          <FadeInContent>
            <PrideCarousel trackIds={['-N49e2WuXv59y3yM7d0i', '-N7IopRxABY4ExIUSyIP', '-Npf3JbhDS6CGtch8W7S', '-OKhtTppYpU8r2obFZ_e', '-NOAdD-OyWu6RPphlsxN']} />
          </FadeInContent>
          <BenefitsContainer>
            {getTrialLanguage}
            <SubscriptionHeader>Dipsea Unlimited</SubscriptionHeader>
            <SubscriptionBody
              style={{ marginBottom: '24px' }}>{props.copyText?.subBody || copy.subBody}</SubscriptionBody>
              <PriceContainer>
                <SubscriptionHeader>$34.99/yr</SubscriptionHeader>
                <SubscribeCTA id="cta-benefits" backgroundColor={getColor(props.color, isInlineCheckout).cta}
                              onClick={(): void => openSubscribePage('pride50')}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
              </PriceContainer>
              <PriceDescription>Allows us to keep stories accessible without cutting corners on quality.</PriceDescription>
              <HR/>
              <PriceContainer>
                <SubscriptionHeader>$47.99/yr</SubscriptionHeader>
                <SubscribeCTA id="cta-benefits" backgroundColor={getColor(props.color, isInlineCheckout).cta}
                              onClick={(): void => openSubscribePage('pride30')}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
              </PriceContainer>
              <PriceDescription>Helps cover the cost of our 7-person team, plus the writers, voice actors, and producers who bring each story to life.</PriceDescription>
              <HR/>
            <PriceContainer>
              <SubscriptionHeader>$69.99/yr</SubscriptionHeader>
              <SubscribeCTA id="cta-benefits" backgroundColor={getColor(props.color, isInlineCheckout).cta}
                            onClick={(): void => openSubscribePage('pride70')}>{props.copyText?.ctaText || copy.ctaText}</SubscribeCTA>
            </PriceContainer>
            <PriceDescription>Covers our full production costs and supports Dipsea’s growth—so we can make even more stories and build new app features!</PriceDescription>

          </BenefitsContainer>
          <FeaturedInHeader>Featured In:</FeaturedInHeader>
          <FeatureImageContainer>
            <Bustle color={colors.white}/>
            <MarieClaire color={colors.white}/>
            <TheGuardian color={colors.white}/>
            <Vox color={colors.white}/>
            <TheNewYorker color={colors.white}/>
            <Vogue color={colors.white}/>
          </FeatureImageContainer>
        </Col>
      </Row>
    </Container>
  )
  const subscribeSection: React.ReactNode = (<ThemeContext.Provider value={props.color}><SubscribeFlow2 isInLine
                                                                                                   confirmationInline={false}/></ThemeContext.Provider>)

  return (
    <>
      <BenefitsLandingPageContainer $background={'linear-gradient(to right, #EE8D54, #C173D6)'} $isTransitioning={isTransitioning}
                                    $isInlineCheckout={isInlineCheckout}>
        <TopNav
          backgroundColor={props.color}
          isDipseaLogoOnly={true}
          isAssetLoaded={true}
        />
        {isInlineCheckout ? subscribeSection : header}
      </BenefitsLandingPageContainer>
      <BenefitsLandingPageContainer $color={'white'}>
        <Container>
          <Row>
            <Col align={'center'}>
              <WhatWomenHeader>What queer listeners are saying...</WhatWomenHeader>
              <CommentSection>{(props.quotes || quotes).map((item: React.ReactNode, index: number): React.ReactNode => {
                return <CommentBox key={`quotes-${index}`}><CommentText>{item}<br/><span>⭐⭐⭐⭐⭐</span></CommentText></CommentBox>
              })}
          </CommentSection>
            </Col>
          </Row>
        </Container>
      </BenefitsLandingPageContainer>
    </>
  )
}

const BenefitsLandingPageContainer = styled.div<any>`
    display: flex;
    flex-direction: column;
    padding-bottom: 60px;
    transition: height 0.5s ease;
    ${(props: any): string => {
      if (props.$color) {
        return `background-color: ${getColor(props.$color, props.$isInlineCheckout).background};`
      } else if (props.$background) {
          return `background: ${props.$background};`
      }
      return ''
    }});

    ${(props: any): any => config(props).media.xs`
    `}
    ${(props: any): any => config(props).media.sm`
    `}
    ${(props: any): any => config(props).media.md`
    `}
    ${(props: any): any => config(props).media.lg`
    `}
    ${(props: any): any => config(props).media.xl`
    `}
`

const Header = styled.h1<any>`
    ${QueensH2};
    font-family: 'Queens Condensed Light';
    text-align: center;
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};
    margin-top: 48px;
    max-width: 500px;
`
const SubHeader = styled.div<any>`
    ${GoodSansB1};
    ${(props: any): string => `
      color: ${getColor(props.$color, false).font};
    `};

    text-align: center;
    margin-top: 24px;
    ${(props: any): any => config(props).media.lg`
      max-width: 1000px;
    `}
`

const BenefitsContainer = styled.div<any>`
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    width: 90%;
    ${(props: any): any => config(props).media.md`
      width: 500px;
    `};
    background-color: ${colors.white};
    border-radius: 24px;
    padding: 24px;
`

const BadgeContainer = styled.div<any>`
    ${GoodSansB4};
    font-family: "Good Sans Bold";
    font-weight: bold;
    width: fit-content;
    color: ${colors.white};
    ${(props: any): string => `
      background-color: ${getColor(props.$color, false).background};
    `};
    border-radius: 8px;
    padding: 8px;
    margin-bottom: 24px;
`
const SubscriptionHeader = styled.h2<any>`
    color: ${colors.midnightBlue};
    font-family: "Queens Condensed Light";
    font-style: normal;
    line-height: 110%;
    margin-block: 0;
    margin: 0;
    padding: 0;
    font-size: 36px;
`

const SubscriptionBody = styled.div<any>`
    ${GoodSansB2};
    padding: 12px;
    text-align: center;
    color: ${colors.midnightBlue};
`

const SubscribeCTA = styled(CTAPrimary)<any>`
    ${(props: any): string => props.$marginTop ? `margin-top: ${props.$marginTop};` : ''};
    ${(props: any): string => props.$marginBottom ? `margin-bottom: ${props.$marginBottom};` : ''};
    background-color: ${colors.pink};
    min-width: 100px;
    &:hover {
     background-color: ${colors.orange};   
    }
`

const FeaturedInHeader = styled.h3<any>`
    ${QueensH3};
    font-family: 'Queens Condensed Light';
    margin-top: 36px;
    margin-bottom: 24px;
`

const FeatureImageContainer = styled.div<any>`
    text-align: center;

    svg {
        width: 180px;
        margin-bottom: 16px;
    }

    max-width: 650px;
    ${(props: any): any => config(props).media.lg`
    svg {
        width: 180px;
    }
    `}
`

const WhatWomenHeader = styled.h2<any>`
    ${QueensH2};
    font-family: 'Queens Condensed Light';
    text-align: center;
    color: ${colors.midnightBlue};
    margin-top: 48px;
    margin-bottom: 24px;
`
const CommentSection = styled.div<any>`
    margin-bottom: 36px;
`

const CommentBox = styled.div<any>`
    border-radius: 16px;
    border: 2px solid #EDEEEF;
    background: rgba(255, 255, 255, 0.00);
    box-shadow: 0px 2px 8px 0px rgba(164, 164, 164, 0.25);
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
`

const CommentText = styled.div<any>`
    ${GoodSansB1};
    color: ${colors.midnightBlue};
    max-width: 500px;
    text-align: center;
`
const PriceContainer = styled.div<any>`
    width: 90%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 12px;
`

const PriceDescription = styled.p<any>`
    width: 90%;
    ${GoodSansB4};
    color: ${colors.midnightBlue};
`

const HR = styled.hr<any>`
    width: 90%;
    margin-top: 24px;
    margin-bottom: 24px;
    border-top: 1px solid ${colors.pink};
`
